import React from 'react';
import { Link } from 'react-router-dom';
import { Tent, Award, Mail, Facebook, ArrowRight, CheckCircle, Zap, Target, Shield, Bot } from 'lucide-react';
import SEO from '../components/SEO';
import { serviceSchema, organizationSchema, breadcrumbSchema } from '../utils/structuredData';

const ServicesPage = () => {
  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 768px) {
          .services-hero-h1 { font-size: 32px !important; letter-spacing: 1px !important; }
          .services-hero-sub { font-size: 15px !important; }
          .services-hero-section { padding: 60px 0 !important; }
          .services-section-h2 { font-size: 26px !important; letter-spacing: 1px !important; }
          .services-cta-h2 { font-size: 26px !important; }
          .services-cta-text { font-size: 16px !important; }
          .services-grid { grid-template-columns: 1fr !important; }
          .services-section-pad { padding: 60px 0 !important; }
          .services-btn { font-size: 18px !important; padding: 14px 20px !important; }

          /* Fix icon + text overflow in service blocks */
          [style*="serviceBlock"], .service-block-wrap { padding: 24px !important; }
          [class*="serviceBlockHeader"] { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
        }
        @media (max-width: 480px) {
          /* On very small screens, stack icon above text and constrain title */
          .svc-block-header { flex-direction: column !important; align-items: flex-start !important; }
          .svc-block-title { font-size: 30px !important; word-break: break-word !important; }
          .svc-block-tagline { font-size: 14px !important; }
          .svc-block-icon { width: 72px !important; height: 72px !important; }
        }
      `}</style>
      
      <SEO
        title="Dealership Event Services — Offsite, Tent & Mail"
        description="Turnkey event marketing: offsite sales events, tent setups, direct mail & Facebook campaigns for dealerships nationwide. Guaranteed results."
        keywords="dealership event services, automotive marketing services, offsite sales events service, tent setup permitting, direct mail marketing dealerships, Facebook event marketing, golf cart event marketing, motorcycle dealer events"
        canonicalUrl="https://driven2win.com/services"
        structuredData={[serviceSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Services',url:'https://driven2win.com/services'}])]}
      />
      
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="services-hero-h1">
            <Zap size={64} style={{ display: 'inline-block', color: '#FFC300', marginRight: '20px' }} />
            SERVICES THAT <span style={{color: '#FFC300'}}>DOMINATE</span>
          </h1>
          <p style={styles.heroSubheadline} className="services-hero-sub">
            From offsite event execution to viral social campaigns, we handle EVERYTHING needed to pack your lot with qualified buyers and move inventory FAST.
          </p>
        </div>
      </section>

      {/* Service Categories */}
      <section style={styles.servicesSection}>
        <div style={styles.container}>
          <ServiceBlock
            icon={<Tent size={80} />}
            color="#E31E24"
            title="Offsite Sales Events"
            tagline="Take Your Dealership Where The Buyers Are"
            description="Don't wait for customers to come to you — GO TO THEM! We create high-traffic offsite sales events at premium locations where thousands of qualified buyers gather. From shopping centers to fairgrounds, we bring your inventory to packed venues that generate MASSIVE foot traffic and serious sales volume."
            features={[
              "Premium venue selection with HIGH foot traffic",
              "Complete event planning & logistics coordination",
              "Professional setup with tents, signage, and branding",
              "Aggressive multi-channel promotion (direct mail + social)",
              "On-site support throughout the entire event",
              "Post-event analysis and ROI reporting"
            ]}
            link="/services/offsite-events"
          />

          <ServiceBlock
            icon={<Award size={80} />}
            color="#FF6B35"
            title="Tent Setups & Permitting"
            tagline="Completely Turnkey Event Infrastructure"
            description="We handle EVERYTHING so you can focus on selling. Our team manages all permits, tent installations, signage, power, lighting, and logistics. Whether it's a 3-day event or a 10-day-long sales blitz, we create a professional, high-impact setup that screams 'BIG EVENT' and attracts serious buyers."
            features={[
              "All permits obtained and processed (we don't miss a thing)",
              "Professional tent installation with weatherproofing",
              "Custom signage and dealership branding",
              "Power, lighting, and infrastructure setup",
              "Compliance with all local regulations",
              "Complete tear-down and site restoration"
            ]}
            link="/services/tent-setups"
          />

          <ServiceBlock
            icon={<Mail size={80} />}
            color="#0080FF"
            title="Direct Mail Marketing"
            tagline="Mailers That Create URGENCY and Fill Your Event"
            description="Forget boring postcards that get tossed in the trash! Our direct mail campaigns create EXCITEMENT, URGENCY, and MASSIVE RESPONSE. We target high-potential buyers in your market with compelling offers that drive traffic to your event and generate serious appointment volume."
            features={[
              "Targeted mailing lists of qualified buyers",
              "Eye-catching designs that DEMAND attention",
              "Compelling offers with clear calls-to-action",
              "Multiple touches to maximize response rates",
              "Trackable ROI with response analysis",
              "Guaranteed delivery and quality control"
            ]}
            link="/services/direct-mail"
          />

          <ServiceBlock
            icon={<Facebook size={80} />}
            color="#FFC300"
            title="Powerful Facebook & Instagram Events"
            tagline="Viral Social Campaigns That Amplify Your Reach"
            description="Dominate your market with Facebook and Instagram campaigns that generate 100K-400K impressions and create MASSIVE awareness for your event. We create scroll-stopping content, target the right audiences, and drive huge engagement that translates into calls, traffic, and sales."
            features={[
              "Facebook Event pages with viral potential",
              "Targeted ad campaigns to qualified buyers",
              "Instagram Stories and Feed content creation",
              "Engagement tactics that build BUZZ",
              "Real-time monitoring and optimization",
              "100K-400K impressions per campaign"
            ]}
            link="/services/facebook-events"
          />

          <ServiceBlock
            icon={<Zap size={80} />}
            color="#0080FF"
            title="AI BDC VOICE & SMS AGENTS"
            tagline="24/7 AI agents that answer every call and follow up every lead automatically"
            description="Never miss a deal again. Our AI BDC agents answer every inbound call, respond to every lead in under 60 seconds, and book appointments directly into your calendar — 24 hours a day, 7 days a week, including nights, weekends, and during events."
            features={[
              "AI voice agent answers every call in under 2 rings",
              "SMS agent follows up every lead in under 60 seconds",
              "Books appointments live — no human required",
              "Works during events to handle the surge in calls",
              "Full CRM integration and lead transcription",
              "Re-engagement sequences for no-show leads",
            ]}
            link="/services/ai-bdc"
          />

          <ServiceBlock
            icon={<Bot size={80} />}
            color="#8B5CF6"
            title="AI BDC — Voice & SMS Agents"
            tagline="24/7 AI That Books Appointments While You Sleep"
            description="Never miss another lead. Our AI Voice and SMS Agents instantly answer every call, follow up with every lead, and book appointments automatically — around the clock, 365 days a year. With human-like conversations in both English and Spanish, your customers won't know they're talking to AI — and they won't care, because the experience is seamless and the appointments get set."
            features={[
              "Instantly answers all inbound calls — zero missed leads",
              "AI Voice and SMS follow-up within seconds of every inquiry",
              "Schedules up to 60% of incoming leads as appointments automatically",
              "Human-like conversations customers can't distinguish from live agents",
              "Native-speaking English and Spanish support",
              "Full appointment tracking: set rates, show rates & conversion metrics",
              "Integrates with your CRM — no manual data entry required"
            ]}
            link="/contact"
          />
        </div>
      </section>

      {/* Why Choose Section */}
      <section style={styles.whySection}>
        <div style={styles.container}>
          <h2 style={styles.sectionH2}>
            WHY DEALERS <span style={{color: '#E31E24'}}>TRUST US</span>
          </h2>
          
          <div style={styles.whyGrid}>
            <WhyCard
              icon={<Target />}
              title="23+ Years of Proven Results"
              description="We've produced 6,500+ events across 50 states with an average gross of $250K+ per event."
            />
            <WhyCard
              icon={<Shield />}
              title="GUARANTEED Results"
              description="We GUARANTEE our results. If we don't deliver, we keep working until you're satisfied."
            />
            <WhyCard
              icon={<Zap />}
              title="Completely Turnkey"
              description="From permits to promotion to setup — we handle EVERYTHING. You focus on selling."
            />
            <WhyCard
              icon={<CheckCircle />}
              title="Nationwide Coverage"
              description="We serve car & truck, golf cart, motorcycle, ATV, and RV dealers in all 50 states."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.container}>
          <h2 style={styles.ctaH2}>
            Ready to <span style={{color: '#FFC300'}}>DOMINATE</span> Your Market?
          </h2>
          <p style={styles.ctaText}>
            Let's create a custom event strategy that packs your lot and crushes your sales goals!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary}>
            Get Your FREE Event Strategy Call
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const ServiceBlock = ({ icon, color, title, tagline, description, features, link }) => (
  <div style={styles.serviceBlock}>
    <div style={styles.serviceBlockHeader} className="svc-block-header">
      <div style={{...styles.serviceBlockIcon, background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`}} className="svc-block-icon">
        {React.cloneElement(icon, { color: 'white' })}
      </div>
      <div>
        <h2 style={{...styles.serviceBlockTitle, color: color}} className="svc-block-title">{title}</h2>
        <p style={styles.serviceBlockTagline} className="svc-block-tagline">{tagline}</p>
      </div>
    </div>
    
    <p style={styles.serviceBlockDescription}>{description}</p>
    
    <div style={styles.featuresList}>
      <h3 style={styles.featuresTitle}>What's Included:</h3>
      {features.map((feature, index) => (
        <div key={index} style={styles.featureItem}>
          <CheckCircle size={20} color={color} style={{flexShrink: 0}} />
          <span>{feature}</span>
        </div>
      ))}
    </div>
    
    <Link to={link} style={{...styles.learnMoreBtn, borderColor: color, color: color}}>
      Learn More About This Service
      <ArrowRight size={20} style={{marginLeft: '8px'}} />
    </Link>
  </div>
);

const WhyCard = ({ icon, title, description }) => (
  <div style={styles.whyCard}>
    <div style={styles.whyIcon}>
      {React.cloneElement(icon, { size: 40, color: '#E31E24' })}
    </div>
    <h3 style={styles.whyTitle}>{title}</h3>
    <p style={styles.whyDescription}>{description}</p>
  </div>
);

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #1A1F3A 0%, #0F1118 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(227, 30, 36, 0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.7',
    position: 'relative',
    zIndex: 1,
  },
  servicesSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  serviceBlock: {
    background: '#FFFFFF',
    borderRadius: '20px',
    padding: '40px',
    marginBottom: '48px',
    border: '3px solid #E5E5E5',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
  },
  serviceBlockHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '32px',
    flexWrap: 'wrap',
  },
  serviceBlockIcon: {
    width: '100px',
    height: '100px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
  serviceBlockTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '42px',
    letterSpacing: '1px',
    marginBottom: '8px',
  },
  serviceBlockTagline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    color: '#666',
    fontWeight: '600',
    fontStyle: 'italic',
  },
  serviceBlockDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '32px',
  },
  featuresList: {
    marginBottom: '32px',
  },
  featuresTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    marginBottom: '20px',
    letterSpacing: '1px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#333',
  },
  learnMoreBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '20px',
    padding: '16px 32px',
    border: '3px solid',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
  whySection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#1A1F3A',
    textAlign: 'center',
    marginBottom: '64px',
    letterSpacing: '2px',
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  whyCard: {
    background: '#F5F5F5',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
  },
  whyIcon: {
    width: '80px',
    height: '80px',
    background: '#FFFFFF',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  },
  whyTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  whyDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#FFFFFF',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#FFFFFF',
    color: '#E31E24',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default ServicesPage;
